/**
 * Реплики с ролями из того, что лежит у записи: сегменты диаризации, если они
 * есть, иначе нарезка хранимого текста по строкам.
 *
 * Общая для прод-шага и харнесса замера (#856 мерил на своей копии, и копия
 * разошлась: харнесс резал текст по строкам и ставил всем `unknown`, то есть
 * мерил не тот вход, что получит прод, — находка Codex на #964). Нумерацию и
 * нарезку длинных реплик делает `prepareTurns` ПОСЛЕ этого шага.
 *
 * Редактирование ПДн сюда не входит: в конвейере оно уже случилось
 * (`piiRedactStep` правит и `transcript.text`, и `diarization[].text` в
 * памяти), а харнесс зовёт провайдера мимо конвейера и редактирует сам.
 */
import { z } from 'zod';

import { speakerRoleFor } from './speaker-labels';
import { flattenTurnText, splitTurnLines } from './turns';

/**
 * Сегмент в том виде, в каком он лежит в `transcripts.diarization`. Роли может
 * не быть вовсе: у записи из ASR есть метка говорящего (`speaker`), а роль
 * проставляет категоризатор — и в прод-данных она есть далеко не всегда
 * (замер 22.09: роль у 640 сегментов из 14 749, #987).
 *
 * Экспортируется, потому что это КОНТРАКТ приёмки, а не деталь: тот, кто готовит
 * сегменты к нарезке, обязан отбирать их этой же схемой, иначе его представление
 * о «пригодной реплике» разойдётся с нарезкой — а между отбором и нарезкой стоит
 * редакция ПДн, которая падает на числовой метке говорящего
 * (`sameSpeakerLabel`), и запись молча выпадает (Codex P2 на #1154).
 */
export const CriterionSegmentSchema = z.object({
  speaker: z.string().optional(),
  // `customer` — словарь хранимых сегментов, `client` — наш. Принимаются оба.
  role: z.enum(['agent', 'customer', 'client', 'system', 'unknown']).optional(),
  text: z.string().optional(),
});

export type CriterionSpeaker = 'agent' | 'client' | 'unknown';

export interface CriterionRawTurn {
  speaker: CriterionSpeaker;
  text: string;
  /**
   * Откуда реплика взялась: индекс в ПЕРЕДАННОМ массиве сегментов (или номер
   * строки, когда границы взяты из текста). Пустые и неразобранные сегменты
   * отбрасываются, поэтому позиция в результате индексу уже не равна.
   *
   * Нужен обратному отображению «номер реплики → момент записи» в панели
   * звонка (#862): номера раздаёт `prepareTurns`, и без этого индекса из
   * номера нельзя вернуться к сегменту, у которого есть время. Искать сегмент
   * по тексту цитаты нельзя: короткая фраза («да», приветствие) встречается в
   * нескольких сегментах, и поиск подстроки уводит на первый (находка Codex
   * на #1121).
   */
  sourceIndex: number;
}

export interface CriterionTurnSource {
  /** Хранимый текст транскрипта — запасной путь, когда диаризации нет. */
  transcript: string;
  /** Сегменты ASR: границы реплик и — когда есть — роль говорящего. */
  diarization: readonly unknown[] | null;
  /**
   * Карта «метка ASR → роль» от категоризатора (`context.speakerRoles`).
   * Используется, когда у самого сегмента роли нет.
   */
  speakerRoles?: Readonly<Record<string, string>>;
}

export interface CriterionRawTurns {
  turns: CriterionRawTurn[];
  /** Откуда взяты границы реплик. Уходит в лог шага и в дамп замера. */
  source: 'diarization' | 'lines';
  /** Сколько реплик получили НЕ `unknown` роль — там же. */
  withRole: number;
}

function toCriterionSpeaker(role: string | undefined): CriterionSpeaker {
  if (role === 'agent') return 'agent';
  if (role === 'customer' || role === 'client') return 'client';
  return 'unknown';
}

/**
 * Роль реплики: сначала роль самого сегмента, затем карта ролей по метке ASR.
 *
 * Порядок именно такой: роль в сегменте проставлена для ЭТОГО куска речи, а
 * карта — утверждение про метку целиком. Расходятся они только там, где
 * категоризатор пометил метку, а сегмент нёс свою роль; доверять общему поверх
 * частного значило бы стирать различие внутри одной метки.
 *
 * Метка ищется в карте `speakerRoleFor` — без учёта регистра (`SPEAKER_0` при
 * диаризации `speaker_0`, #1432).
 */
function segmentSpeaker(
  segment: { speaker?: string; role?: string },
  speakerRoles: Readonly<Record<string, string>> | undefined
): CriterionSpeaker {
  const own = toCriterionSpeaker(segment.role);
  if (own !== 'unknown') return own;
  if (segment.speaker === undefined || speakerRoles === undefined) return 'unknown';
  return toCriterionSpeaker(speakerRoleFor(segment.speaker, speakerRoles));
}

export function turnsFromTranscript(source: CriterionTurnSource): CriterionRawTurns {
  const segments = Array.isArray(source.diarization)
    ? source.diarization.flatMap((raw, sourceIndex): CriterionRawTurn[] => {
        const parsed = CriterionSegmentSchema.safeParse(raw);
        if (!parsed.success) return [];
        const text = flattenTurnText(parsed.data.text ?? '');
        if (text === '') return [];
        return [
          {
            speaker: segmentSpeaker(parsed.data, source.speakerRoles),
            text,
            sourceIndex,
          },
        ];
      })
    : [];
  // Одна реплика на весь разговор диаризацией не считается: границ в ней нет.
  if (segments.length > 1) {
    return {
      turns: segments,
      source: 'diarization',
      withRole: segments.filter((s) => s.speaker !== 'unknown').length,
    };
  }
  return {
    turns: splitTurnLines(source.transcript).map((text, sourceIndex) => ({
      speaker: 'unknown' as const,
      text,
      sourceIndex,
    })),
    source: 'lines',
    withRole: 0,
  };
}
